import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, finalize } from 'rxjs';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import {
  DateRangeMode,
  ReportAccessGrant,
  ReportChartResult,
  ReportColumn,
  ReportConfiguration,
  ReportDataType,
  ReportDefinition,
  ReportExecution,
  ReportFilter,
  ReportFilterOption,
  ReportPreview,
  ReportQuery,
  ReportSchedule
} from '../../core/models/reporting.model';
import { AuthService } from '../../core/services/auth.service';
import { ReportingService } from '../../core/services/reporting.service';
import { ConfirmationService } from '../../core/services/confirmation.service';

type WorkspaceSection = 'explore' | 'saved' | 'schedules' | 'history' | 'access';

interface FilterOptionState {
  options: ReportFilterOption[];
  search: string;
  loading: boolean;
  hasMore: boolean;
  continuationToken: string | null;
  error: string;
}

@Component({
  selector: 'app-reporting-workspace',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reporting-workspace.component.html',
  styleUrls: ['./reporting-workspace.component.css']
})
export class ReportingWorkspaceComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly Math = Math;
  readonly weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  readonly frequencies = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
  readonly dateRangeModes: Array<{ value: DateRangeMode; label: string }> = [
    { value: 'PreviousDay', label: 'Previous calendar day' },
    { value: 'PreviousWeek', label: 'Previous calendar week' },
    { value: 'PreviousMonth', label: 'Previous calendar month' },
    { value: 'PreviousYear', label: 'Previous calendar year' },
    { value: 'RollingDays', label: 'Rolling number of days' },
    { value: 'Fixed', label: 'Fixed date range' }
  ];
  readonly timeZones = ['Africa/Johannesburg', 'UTC', 'Africa/Harare', 'Africa/Maputo'];

  activeSection: WorkspaceSection = 'explore';
  catalogue: ReportDefinition[] = [];
  selectedReport?: ReportDefinition;
  filterValues: Record<string, unknown> = {};
  selectedColumns: string[] = [];
  selectedCharts: string[] = [];
  sortColumn = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  page = 1;
  pageSize = 100;
  preview?: ReportPreview;
  configurations: ReportConfiguration[] = [];
  schedules: ReportSchedule[] = [];
  executions: ReportExecution[] = [];
  accessGrants: ReportAccessGrant[] = [];
  isLoadingCatalogue = true;
  isRunning = false;
  isExporting = false;
  isLoadingSection = false;
  errorMessage = '';
  noticeMessage = '';
  fieldErrors: Record<string, string> = {};
  filterOptionStates: Record<string, FilterOptionState> = {};

  showSaveEditor = false;
  configurationName = '';
  editingConfigurationID?: number;
  timeZoneID = 'Africa/Johannesburg';
  accentColour = '#4B1305';
  includeOrganisationBranding = true;

  showScheduleEditor = false;
  editingSchedule?: ReportSchedule;
  scheduleForm: ReportSchedule = this.emptySchedule();
  recipientEntry = '';
  scheduleErrors: Record<string, string> = {};

  showAccessEditor = false;
  accessForm: Omit<ReportAccessGrant, 'reportAccessGrantID'> = this.emptyGrant();

  private readonly currentUser = this.auth.getCurrentUser();
  private readonly optionRequests: Record<string, Subscription> = {};
  private readonly optionSearchTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  readonly isOperator = normalizeUserRole(
    this.currentUser?.userRoleName || this.currentUser?.userRole || this.currentUser?.userRoleID
  ) === UserRole.OPERATOR;
  readonly canAdministerAccess = [UserRole.ADMIN, UserRole.PLATFORM_EMPLOYEE].includes(normalizeUserRole(
    this.currentUser?.userRoleName || this.currentUser?.userRole || this.currentUser?.userRoleID
  ));

  constructor(
    private reporting: ReportingService,
    private confirmations: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.reporting.getCatalogue().pipe(
      finalize(() => this.isLoadingCatalogue = false)
    ).subscribe({
      next: catalogue => {
        this.catalogue = catalogue;
        if (catalogue.length) this.selectReport(catalogue[0].key);
      },
      error: error => this.handleError(error, 'Unable to load the report catalogue.')
    });
  }

  ngOnDestroy(): void {
    this.cancelAllOptionRequests();
  }

  setSection(section: WorkspaceSection): void {
    this.activeSection = section;
    this.errorMessage = '';
    if (section === 'saved') this.loadConfigurations();
    if (section === 'schedules') {
      this.loadConfigurations();
      this.loadSchedules();
    }
    if (section === 'history') this.loadExecutions();
    if (section === 'access') this.loadAccessGrants();
  }

  selectReport(key: string): void {
    const report = this.catalogue.find(item => item.key === key);
    if (!report) return;
    this.selectedReport = report;
    this.filterValues = {};
    this.selectedColumns = report.columns.filter(column => column.default).map(column => column.key);
    if (!this.selectedColumns.length && report.columns.length) this.selectedColumns = [report.columns[0].key];
    this.selectedCharts = [];
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.page = 1;
    this.preview = undefined;
    this.noticeMessage = '';
    this.fieldErrors = {};
    this.editingConfigurationID = undefined;
    this.configurationName = '';
    if (this.isOperator && this.currentUser?.organisationID) {
      this.filterValues['organisationID'] = this.currentUser.organisationID;
    }
    this.initialiseFilterOptions();
  }

  visibleFilters(): ReportFilter[] {
    return (this.selectedReport?.filters || []).filter(filter => !this.isFilterHidden(filter));
  }

  isRemoteOptionFilter(filter: ReportFilter): boolean {
    return filter.control === 'select' && filter.optionSource === 'report-filter-options';
  }

  filterControl(filter: ReportFilter): 'text' | 'number' | 'datetime' | 'boolean' | 'select' {
    if (filter.options?.length) return 'select';
    if (filter.control) return filter.control;
    if (filter.dataType === 'integer' || filter.dataType === 'decimal') return 'number';
    if (filter.dataType === 'string') return 'text';
    return filter.dataType;
  }

  optionState(filter: ReportFilter): FilterOptionState {
    return this.filterOptionStates[filter.key] || this.emptyOptionState();
  }

  isOptionFilterDisabled(filter: ReportFilter): boolean {
    return (filter.dependsOn || []).some(key => {
      const value = this.filterValues[key];
      return value === null || value === undefined || value === '';
    });
  }

  optionDependencyHint(filter: ReportFilter): string {
    const missing = (filter.dependsOn || [])
      .filter(key => this.filterValues[key] === null || this.filterValues[key] === undefined || this.filterValues[key] === '')
      .map(key => this.selectedReport?.filters.find(item => item.key === key)?.label || key);
    return missing.length ? `Select ${missing.join(' and ')} first.` : '';
  }

  onFilterValueChange(filter: ReportFilter, value: unknown): void {
    this.filterValues[filter.key] = value === '' ? null : value;
    this.fieldErrors[filter.key] = '';
    this.preview = undefined;
    this.page = 1;
    this.clearDownstreamFilters(filter.key);
    this.loadNewlyAvailableOptionFilters(filter.key);
  }

  searchFilterOptions(filter: ReportFilter, search: string): void {
    const state = this.ensureOptionState(filter.key);
    state.search = search;
    clearTimeout(this.optionSearchTimers[filter.key]);
    this.optionRequests[filter.key]?.unsubscribe();
    state.loading = true;
    this.optionSearchTimers[filter.key] = setTimeout(() => this.loadFilterOptions(filter), 300);
  }

  loadMoreFilterOptions(filter: ReportFilter): void {
    const state = this.ensureOptionState(filter.key);
    if (!state.hasMore || state.loading || !state.continuationToken) return;
    this.loadFilterOptions(filter, true);
  }

  get schedulableConfigurations(): ReportConfiguration[] {
    return this.configurations.filter(configuration =>
      this.catalogue.find(report => report.key === configuration.reportKey)?.canSchedule === true
    );
  }

  applyPassengerPreset(period: 'today' | 'week' | 'month'): void {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (period === 'week') {
      const mondayOffset = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - mondayOffset);
    }
    if (period === 'month') start.setDate(1);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    this.filterValues['fromUtc'] = this.toLocalInput(start.toISOString());
    this.filterValues['toUtc'] = this.toLocalInput(end.toISOString());
    this.noticeMessage = `Applied the new passengers ${period} date range.`;
  }

  setColumn(key: string, checked: boolean): void {
    this.selectedColumns = checked
      ? Array.from(new Set([...this.selectedColumns, key]))
      : this.selectedColumns.filter(item => item !== key);
    if (this.sortColumn === key && !checked) this.sortColumn = '';
  }

  setChart(key: string, checked: boolean): void {
    this.selectedCharts = checked
      ? Array.from(new Set([...this.selectedCharts, key]))
      : this.selectedCharts.filter(item => item !== key);
  }

  isSelected(collection: string[], key: string): boolean {
    return collection.includes(key);
  }

  runReport(page = 1): void {
    if (!this.selectedReport || !this.validateQuery()) return;
    this.page = page;
    this.isRunning = true;
    this.errorMessage = '';
    this.reporting.preview(this.selectedReport.key, this.buildQuery()).pipe(
      finalize(() => this.isRunning = false)
    ).subscribe({
      next: preview => this.preview = {
        ...preview,
        rows: preview.rows || [],
        columns: preview.columns || [],
        summaries: preview.summaries || [],
        charts: preview.charts || [],
        warnings: preview.warnings || []
      },
      error: error => this.handleError(error, 'Unable to run this report.')
    });
  }

  downloadExcel(): void {
    if (!this.selectedReport?.canExport || !this.validateQuery()) return;
    this.isExporting = true;
    this.errorMessage = '';
    this.reporting.export(
      this.selectedReport.key,
      this.buildQuery(),
      this.timeZoneID,
      this.includeOrganisationBranding,
      this.accentColour
    ).pipe(finalize(() => this.isExporting = false)).subscribe({
      next: file => this.saveFile(file.blob, file.fileName),
      error: error => this.handleBlobError(error, 'Unable to generate the Excel workbook.')
    });
  }

  openSaveEditor(): void {
    if (!this.selectedReport || !this.validateQuery()) return;
    if (!this.editingConfigurationID) this.configurationName = '';
    this.showSaveEditor = true;
  }

  saveConfiguration(): void {
    if (!this.selectedReport || !this.configurationName.trim()) {
      this.fieldErrors['configurationName'] = 'Enter a configuration name.';
      return;
    }
    const payload = {
      name: this.configurationName.trim(),
      reportKey: this.selectedReport.key,
      query: this.buildQuery(),
      timeZoneID: this.timeZoneID,
      accentColour: this.accentColour,
      includeOrganisationBranding: this.includeOrganisationBranding
    };
    const request = this.editingConfigurationID
      ? this.reporting.updateConfiguration({ ...payload, reportConfigurationID: this.editingConfigurationID })
      : this.reporting.createConfiguration(payload);
    request.subscribe({
      next: () => {
        this.showSaveEditor = false;
        this.noticeMessage = 'Report configuration saved.';
        this.loadConfigurations();
      },
      error: error => this.handleError(error, 'Unable to save this configuration.')
    });
  }

  loadConfigurations(): void {
    this.isLoadingSection = true;
    this.reporting.getConfigurations().pipe(finalize(() => this.isLoadingSection = false)).subscribe({
      next: values => this.configurations = values,
      error: error => this.handleError(error, 'Unable to load saved reports.')
    });
  }

  openConfiguration(configuration: ReportConfiguration): void {
    this.reporting.getConfiguration(configuration.reportConfigurationID).subscribe({
      next: detail => {
        const report = this.catalogue.find(item => item.key === detail.reportKey);
        if (!report) {
          this.errorMessage = 'This report definition is no longer available.';
          return;
        }
        this.selectReport(report.key);
        const available = new Set(report.columns.map(column => column.key));
        const missing = (detail.query.columns || []).filter(key => !available.has(key));
        this.selectedColumns = (detail.query.columns || []).filter(key => available.has(key));
        if (!this.selectedColumns.length) this.selectedColumns = report.columns.filter(column => column.default).map(column => column.key);
        this.selectedCharts = (detail.query.charts || []).filter(key => report.charts.some(chart => chart.key === key));
        this.sortColumn = detail.query.sort?.[0]?.column || '';
        this.sortDirection = detail.query.sort?.[0]?.direction || 'asc';
        this.pageSize = detail.query.pageSize || 100;
        for (const filter of report.filters) {
          const value = detail.query[filter.key];
          this.filterValues[filter.key] = filter.dataType === 'datetime' && value
            ? this.toLocalInput(String(value))
            : value;
        }
        if (this.isOperator && this.currentUser?.organisationID) this.filterValues['organisationID'] = this.currentUser.organisationID;
        this.reloadAvailableOptionFilters();
        this.timeZoneID = detail.timeZoneID || 'Africa/Johannesburg';
        this.accentColour = detail.accentColour || '#4B1305';
        this.includeOrganisationBranding = detail.includeOrganisationBranding !== false;
        this.configurationName = detail.name;
        this.editingConfigurationID = detail.reportConfigurationID;
        this.activeSection = 'explore';
        this.noticeMessage = missing.length
          ? `${missing.length} saved column${missing.length === 1 ? ' is' : 's are'} no longer available and was omitted.`
          : 'Saved configuration loaded. Select Run report when ready.';
      },
      error: error => this.handleError(error, 'Unable to open this saved report.')
    });
  }

  updateLoadedConfiguration(): void {
    if (!this.editingConfigurationID) return;
    this.showSaveEditor = true;
  }

  async deleteConfiguration(configuration: ReportConfiguration): Promise<void> {
    if (!await this.confirmations.confirm({
      title: 'Delete saved report',
      message: `Delete "${configuration.name}"? Its schedules will also be deleted. Existing run history will remain.`,
      confirmLabel: 'Delete',
      tone: 'danger'
    })) return;
    this.reporting.deleteConfiguration(configuration.reportConfigurationID).subscribe({
      next: () => {
        this.noticeMessage = 'Saved report and its schedules were deleted.';
        this.loadConfigurations();
      },
      error: error => this.handleError(error, 'Unable to delete this saved report.')
    });
  }

  loadSchedules(): void {
    this.isLoadingSection = true;
    this.reporting.getSchedules().pipe(finalize(() => this.isLoadingSection = false)).subscribe({
      next: values => this.schedules = values,
      error: error => this.handleError(error, 'Unable to load schedules.')
    });
  }

  openScheduleEditor(schedule?: ReportSchedule): void {
    if (!schedule && !this.schedulableConfigurations.length) return;
    this.editingSchedule = schedule;
    this.scheduleForm = schedule ? { ...schedule, recipients: [...(schedule.recipients || [])] } : this.emptySchedule();
    if (!schedule) this.scheduleForm.reportConfigurationID = this.schedulableConfigurations[0].reportConfigurationID;
    this.recipientEntry = '';
    this.scheduleErrors = {};
    this.showScheduleEditor = true;
  }

  addRecipient(): void {
    const candidates = this.recipientEntry.split(/[,\s;]+/).map(value => value.trim().toLowerCase()).filter(Boolean);
    const invalid = candidates.find(value => !this.isEmail(value));
    if (invalid) {
      this.scheduleErrors['recipients'] = `"${invalid}" is not a valid email address.`;
      return;
    }
    this.scheduleForm.recipients = Array.from(new Set([...this.scheduleForm.recipients, ...candidates])).slice(0, 25);
    this.recipientEntry = '';
    this.scheduleErrors['recipients'] = '';
  }

  removeRecipient(recipient: string): void {
    this.scheduleForm.recipients = this.scheduleForm.recipients.filter(value => value !== recipient);
  }

  async saveSchedule(): Promise<void> {
    if (this.recipientEntry.trim()) this.addRecipient();
    if (!this.validateSchedule()) return;
    const knownDomain = (this.currentUser?.userEmail || this.currentUser?.username || '').split('@')[1]?.toLowerCase();
    const hasExternalRecipient = knownDomain && this.scheduleForm.recipients.some(email => email.split('@')[1] !== knownDomain);
    if (hasExternalRecipient && !await this.confirmations.confirm({
      title: 'Confirm external delivery',
      message: `One or more recipients are outside ${knownDomain}. Continue with this sensitive delivery decision?`,
      confirmLabel: 'Continue delivery',
      tone: 'danger'
    })) return;
    const payload = this.normalizedSchedule();
    const request = this.editingSchedule
      ? this.reporting.updateSchedule({ ...payload, reportScheduleID: this.editingSchedule.reportScheduleID })
      : this.reporting.createSchedule(payload);
    request.subscribe({
      next: () => {
        this.showScheduleEditor = false;
        this.noticeMessage = 'Schedule saved.';
        this.loadSchedules();
      },
      error: error => this.handleError(error, 'Unable to save this schedule.')
    });
  }

  async runSchedule(schedule: ReportSchedule): Promise<void> {
    if (schedule.isRunning || !await this.confirmations.confirm({
      title: 'Run scheduled report now',
      message: `Run "${schedule.name}" now? This sends a real report to its configured recipients.`,
      confirmLabel: 'Run and send'
    })) return;
    this.reporting.runSchedule(schedule.reportScheduleID).subscribe({
      next: () => {
        this.noticeMessage = 'The report delivery was queued.';
        this.loadSchedules();
      },
      error: error => this.handleError(error, 'Unable to run this schedule now.')
    });
  }

  async deleteSchedule(schedule: ReportSchedule): Promise<void> {
    if (schedule.isRunning || !await this.confirmations.confirm({
      title: 'Delete report schedule',
      message: `Delete the schedule "${schedule.name}"?`,
      confirmLabel: 'Delete',
      tone: 'danger'
    })) return;
    this.reporting.deleteSchedule(schedule.reportScheduleID).subscribe({
      next: () => {
        this.noticeMessage = 'Schedule deleted.';
        this.loadSchedules();
      },
      error: error => this.handleError(error, 'Unable to delete this schedule.')
    });
  }

  loadExecutions(): void {
    this.isLoadingSection = true;
    this.reporting.getExecutions(50).pipe(finalize(() => this.isLoadingSection = false)).subscribe({
      next: values => this.executions = values,
      error: error => this.handleError(error, 'Unable to load report run history.')
    });
  }

  downloadExecution(execution: ReportExecution): void {
    if (!execution.hasFile) return;
    this.reporting.downloadExecution(execution).subscribe({
      next: file => this.saveFile(file.blob, file.fileName),
      error: error => this.handleBlobError(error, 'Unable to download this retained workbook.')
    });
  }

  loadAccessGrants(): void {
    if (!this.canAdministerAccess) return;
    this.isLoadingSection = true;
    this.reporting.getAccessGrants().pipe(finalize(() => this.isLoadingSection = false)).subscribe({
      next: values => this.accessGrants = values,
      error: error => this.handleError(error, 'Unable to load report access grants.')
    });
  }

  openAccessEditor(): void {
    this.accessForm = this.emptyGrant();
    this.showAccessEditor = true;
  }

  async saveAccessGrant(): Promise<void> {
    if ((this.accessForm.userID && this.accessForm.role) || (!this.accessForm.userID && !this.accessForm.role)) {
      this.fieldErrors['grantSubject'] = 'Specify either a user ID or a role, but not both.';
      return;
    }
    if (this.accessForm.canViewSensitiveFields && !await this.confirmations.confirm({
      title: 'Grant sensitive-field access',
      message: 'This may expose email, phone, MAC and IP data. Do you want to grant this capability?',
      confirmLabel: 'Grant access',
      tone: 'danger'
    })) return;
    if (this.accessForm.canSchedule && !await this.confirmations.confirm({
      title: 'Grant scheduled delivery',
      message: 'Reports may be delivered to external email recipients. Do you want to grant this capability?',
      confirmLabel: 'Grant capability',
      tone: 'danger'
    })) return;
    this.reporting.createAccessGrant(this.accessForm).subscribe({
      next: () => {
        this.showAccessEditor = false;
        this.noticeMessage = 'Access override saved.';
        this.loadAccessGrants();
      },
      error: error => this.handleError(error, 'Unable to save this access grant.')
    });
  }

  async deleteAccessGrant(grant: ReportAccessGrant): Promise<void> {
    if (!await this.confirmations.confirm({
      title: 'Delete access override',
      message: 'Delete this report access override?',
      confirmLabel: 'Delete',
      tone: 'danger'
    })) return;
    this.reporting.deleteAccessGrant(grant.reportAccessGrantID).subscribe({
      next: () => this.loadAccessGrants(),
      error: error => this.handleError(error, 'Unable to delete this access grant.')
    });
  }

  formatValue(value: unknown, dataType: ReportDataType, columnKey?: string): string {
    if (value === null || value === undefined || value === '') return '—';
    if (dataType === 'datetime') {
      const date = new Date(String(value));
      return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium', timeStyle: 'short'
      }).format(date);
    }
    if (dataType === 'decimal') {
      const formatted = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(value));
      return columnKey?.toLowerCase().includes('datausage') ? `${formatted} MB` : formatted;
    }
    if (dataType === 'integer') return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(value));
    if (dataType === 'boolean') return value === true || String(value).toLowerCase() === 'true' ? 'Yes' : 'No';
    return String(value);
  }

  attributionHelp(value: unknown): string {
    const help: Record<string, string> = {
      Exact: 'The hotspot session is explicitly linked to the trip.',
      TimeWindowMatch: 'Vehicle and session time identify one trip.',
      Ambiguous: 'More than one vehicle trip overlaps the session.',
      Unmatched: 'No trip could be attributed to this session.'
    };
    return help[String(value)] || '';
  }

  isLowCoverage(row: Record<string, unknown>, column: ReportColumn): boolean {
    return column.key.toLowerCase().includes('gpscoverage') && Number(row[column.key]) < 50;
  }

  totalPages(): number {
    if (!this.preview) return 1;
    return Math.max(1, Math.ceil(this.preview.totalRows / this.preview.pageSize));
  }

  chartMax(chart: ReportChartResult): number {
    return Math.max(1, ...chart.points.map(point => Number(point.value) || 0));
  }

  chartPercent(value: number, chart: ReportChartResult): number {
    return Math.max(2, (Number(value) / this.chartMax(chart)) * 100);
  }

  linePoints(chart: ReportChartResult): string {
    if (!chart.points.length) return '';
    const max = this.chartMax(chart);
    const divisor = Math.max(1, chart.points.length - 1);
    return chart.points.map((point, index) => `${(index / divisor) * 100},${100 - (Number(point.value) / max) * 88}`).join(' ');
  }

  doughnutStyle(chart: ReportChartResult): string {
    const total = chart.points.reduce((sum, point) => sum + Number(point.value || 0), 0) || 1;
    let cursor = 0;
    const colours = ['#4b1305', '#f6c21a', '#0f766e', '#7a2a10', '#64748b'];
    const stops = chart.points.map((point, index) => {
      const start = cursor;
      cursor += Number(point.value || 0) / total * 100;
      return `${colours[index % colours.length]} ${start}% ${cursor}%`;
    });
    return `conic-gradient(${stops.join(',')})`;
  }

  trackByKey(_index: number, item: { key: string }): string {
    return item.key;
  }

  trackByID(_index: number, item: any): number {
    return item.reportConfigurationID || item.reportScheduleID || item.reportExecutionID || item.reportAccessGrantID || _index;
  }

  private buildQuery(): ReportQuery {
    const query: ReportQuery = {
      columns: [...this.selectedColumns],
      sort: this.sortColumn ? [{ column: this.sortColumn, direction: this.sortDirection }] : [],
      charts: [...this.selectedCharts],
      page: this.page,
      pageSize: Number(this.pageSize)
    };
    for (const filter of this.selectedReport?.filters || []) {
      let value = this.filterValues[filter.key];
      if (value === '' || value === undefined) value = null;
      if (filter.dataType === 'datetime' && value) value = new Date(String(value)).toISOString();
      if (filter.dataType === 'integer' && value !== null) value = Number(value);
      query[filter.key] = value;
    }
    if (this.isOperator && this.currentUser?.organisationID) query.organisationID = this.currentUser.organisationID;
    return query;
  }

  private initialiseFilterOptions(): void {
    this.cancelAllOptionRequests();
    this.filterOptionStates = {};
    for (const filter of this.selectedReport?.filters || []) {
      if (this.isRemoteOptionFilter(filter)) {
        this.filterOptionStates[filter.key] = this.emptyOptionState();
      }
    }
    this.reloadAvailableOptionFilters();
  }

  private reloadAvailableOptionFilters(): void {
    for (const filter of this.selectedReport?.filters || []) {
      if (this.isRemoteOptionFilter(filter) && !this.isFilterHidden(filter) && !this.isOptionFilterDisabled(filter)) {
        this.loadFilterOptions(filter);
      }
    }
  }

  private loadNewlyAvailableOptionFilters(upstreamKey: string): void {
    for (const filter of this.selectedReport?.filters || []) {
      if (
        this.isRemoteOptionFilter(filter)
        && (filter.dependsOn || []).includes(upstreamKey)
        && !this.isOptionFilterDisabled(filter)
      ) {
        this.loadFilterOptions(filter);
      }
    }
  }

  private loadFilterOptions(filter: ReportFilter, append = false): void {
    if (!this.selectedReport || this.isOptionFilterDisabled(filter)) return;
    const state = this.ensureOptionState(filter.key);
    this.optionRequests[filter.key]?.unsubscribe();
    clearTimeout(this.optionSearchTimers[filter.key]);
    state.loading = true;
    state.error = '';
    if (!append) {
      const selected = this.filterValues[filter.key];
      const selectedOption = state.search.trim()
        ? state.options.find(option => String(option.value) === String(selected))
        : undefined;
      state.options = selectedOption ? [selectedOption] : [];
      state.hasMore = false;
      state.continuationToken = null;
    }

    const reportKey = this.selectedReport.key;
    const searchAtRequest = state.search.trim();
    this.optionRequests[filter.key] = this.reporting.getFilterOptions(
      reportKey,
      filter.key,
      this.filterValues,
      searchAtRequest,
      50,
      append ? state.continuationToken : null
    ).pipe(finalize(() => state.loading = false)).subscribe({
      next: response => {
        if (this.selectedReport?.key !== reportKey || state.search.trim() !== searchAtRequest) return;
        const incoming = response.options || [];
        state.options = append || Boolean(searchAtRequest && state.options.length)
          ? this.mergeFilterOptions(state.options, incoming)
          : incoming;
        state.hasMore = response.hasMore === true;
        state.continuationToken = response.continuationToken || null;

        const selected = this.filterValues[filter.key];
        if (
          !append
          && !searchAtRequest
          && selected !== null
          && selected !== undefined
          && selected !== ''
          && !state.hasMore
          && !state.options.some(option => String(option.value) === String(selected))
        ) {
          this.filterValues[filter.key] = null;
          this.clearDownstreamFilters(filter.key);
          this.noticeMessage = `${filter.label} from the saved or previous selection is no longer available and was cleared.`;
        }
      },
      error: error => this.handleFilterOptionError(filter, error)
    });
  }

  private clearDownstreamFilters(upstreamKey: string): void {
    for (const filter of this.selectedReport?.filters || []) {
      if (!(filter.dependsOn || []).includes(upstreamKey)) continue;
      this.filterValues[filter.key] = null;
      this.fieldErrors[filter.key] = '';
      this.cancelOptionRequest(filter.key);
      this.filterOptionStates[filter.key] = this.emptyOptionState();
      this.clearDownstreamFilters(filter.key);
    }
  }

  private handleFilterOptionError(filter: ReportFilter, error: any): void {
    const body = error?.error;
    const errors = body?.errors && typeof body.errors === 'object' ? body.errors : {};
    const errorKeys = Object.keys(errors);

    for (const key of errorKeys) {
      const value = errors[key];
      this.fieldErrors[key] = Array.isArray(value) ? value.join(' ') : String(value);
      if (this.selectedReport?.filters.some(item => item.key === key)) {
        this.filterValues[key] = null;
        this.clearDownstreamFilters(key);
      }
    }

    const message = body?.message || body?.title || 'Unable to load filter options.';
    const state = this.ensureOptionState(filter.key);
    state.error = message;
    if (!errorKeys.length) this.fieldErrors[filter.key] = message;
    if ([403, 404].includes(Number(error?.status))) this.handleError(error, message);
  }

  private mergeFilterOptions(existing: ReportFilterOption[], incoming: ReportFilterOption[]): ReportFilterOption[] {
    const values = new Set(existing.map(option => String(option.value)));
    return [...existing, ...incoming.filter(option => !values.has(String(option.value)))];
  }

  private ensureOptionState(key: string): FilterOptionState {
    return this.filterOptionStates[key] ||= this.emptyOptionState();
  }

  private emptyOptionState(): FilterOptionState {
    return {
      options: [],
      search: '',
      loading: false,
      hasMore: false,
      continuationToken: null,
      error: ''
    };
  }

  private cancelOptionRequest(key: string): void {
    clearTimeout(this.optionSearchTimers[key]);
    this.optionRequests[key]?.unsubscribe();
    delete this.optionSearchTimers[key];
    delete this.optionRequests[key];
  }

  private cancelAllOptionRequests(): void {
    for (const key of Object.keys(this.optionRequests)) this.cancelOptionRequest(key);
    for (const key of Object.keys(this.optionSearchTimers)) clearTimeout(this.optionSearchTimers[key]);
  }

  private isFilterHidden(filter: ReportFilter): boolean {
    return this.isOperator && filter.key === 'organisationID';
  }

  private validateQuery(): boolean {
    this.fieldErrors = {};
    if (!this.selectedColumns.length) this.fieldErrors['columns'] = 'Select at least one column.';
    if (this.pageSize < 1 || this.pageSize > 500) this.fieldErrors['pageSize'] = 'Page size must be between 1 and 500.';
    for (const filter of this.selectedReport?.filters || []) {
      if (filter.required && (this.filterValues[filter.key] === '' || this.filterValues[filter.key] === null || this.filterValues[filter.key] === undefined)) {
        this.fieldErrors[filter.key] = `${filter.label} is required.`;
      }
    }
    const from = this.filterValues['fromUtc'] ? new Date(String(this.filterValues['fromUtc'])) : null;
    const to = this.filterValues['toUtc'] ? new Date(String(this.filterValues['toUtc'])) : null;
    if (from && to) {
      if (to <= from) this.fieldErrors['toUtc'] = 'The exclusive end must be after the start.';
      if (to.getTime() - from.getTime() > 366 * 86400000) this.fieldErrors['toUtc'] = 'Date ranges may not exceed 366 days.';
    }
    return !Object.values(this.fieldErrors).some(Boolean);
  }

  private validateSchedule(): boolean {
    this.scheduleErrors = {};
    const value = this.scheduleForm;
    if (!value.name.trim()) this.scheduleErrors['name'] = 'Enter a schedule name.';
    if (!value.reportConfigurationID) this.scheduleErrors['configuration'] = 'Select a saved report.';
    if (!value.runAtLocalTime) this.scheduleErrors['runAt'] = 'Select a local run time.';
    if (value.frequency === 'Weekly' && (value.dayOfWeek === null || value.dayOfWeek === undefined)) this.scheduleErrors['dayOfWeek'] = 'Select a weekday.';
    if (['Monthly', 'Yearly'].includes(value.frequency) && (!value.dayOfMonth || value.dayOfMonth < 1 || value.dayOfMonth > 31)) {
      this.scheduleErrors['dayOfMonth'] = 'Enter a day from 1 to 31.';
    }
    if (value.frequency === 'Yearly' && (!value.monthOfYear || value.monthOfYear < 1 || value.monthOfYear > 12)) {
      this.scheduleErrors['monthOfYear'] = 'Enter a month from 1 to 12.';
    }
    if (value.dateRangeMode === 'RollingDays' && (!value.rollingWindowDays || value.rollingWindowDays < 1 || value.rollingWindowDays > 366)) {
      this.scheduleErrors['rollingWindowDays'] = 'Enter 1 to 366 days.';
    }
    if (value.dateRangeMode === 'Fixed' && (!value.fixedFromUtc || !value.fixedToUtc)) this.scheduleErrors['fixedRange'] = 'Enter both fixed range boundaries.';
    if (value.dateRangeMode === 'Fixed' && value.fixedFromUtc && value.fixedToUtc && new Date(value.fixedToUtc) <= new Date(value.fixedFromUtc)) {
      this.scheduleErrors['fixedRange'] = 'The exclusive end must be after the start.';
    }
    if (!value.recipients.length || value.recipients.length > 25) this.scheduleErrors['recipients'] = 'Add between 1 and 25 valid email recipients.';
    return !Object.values(this.scheduleErrors).some(Boolean);
  }

  private normalizedSchedule(): Omit<ReportSchedule, 'reportScheduleID'> {
    const value = this.scheduleForm;
    return {
      reportConfigurationID: value.reportConfigurationID,
      name: value.name.trim(),
      enabled: value.enabled,
      frequency: value.frequency,
      runAtLocalTime: value.runAtLocalTime.length === 5 ? `${value.runAtLocalTime}:00` : value.runAtLocalTime,
      dayOfWeek: value.frequency === 'Weekly' ? Number(value.dayOfWeek) : null,
      dayOfMonth: ['Monthly', 'Yearly'].includes(value.frequency) ? Number(value.dayOfMonth) : null,
      monthOfYear: value.frequency === 'Yearly' ? Number(value.monthOfYear) : null,
      timeZoneID: value.timeZoneID,
      dateRangeMode: value.dateRangeMode,
      rollingWindowDays: value.dateRangeMode === 'RollingDays' ? Number(value.rollingWindowDays) : null,
      fixedFromUtc: value.dateRangeMode === 'Fixed' && value.fixedFromUtc ? new Date(value.fixedFromUtc).toISOString() : null,
      fixedToUtc: value.dateRangeMode === 'Fixed' && value.fixedToUtc ? new Date(value.fixedToUtc).toISOString() : null,
      recipients: Array.from(new Set(value.recipients.map(email => email.toLowerCase()))),
      outputFormat: 'xlsx'
    };
  }

  private emptySchedule(): ReportSchedule {
    return {
      reportScheduleID: 0,
      reportConfigurationID: 0,
      name: '',
      enabled: true,
      frequency: 'Daily',
      runAtLocalTime: '06:00',
      dayOfWeek: null,
      dayOfMonth: null,
      monthOfYear: null,
      timeZoneID: 'Africa/Johannesburg',
      dateRangeMode: 'PreviousDay',
      rollingWindowDays: null,
      fixedFromUtc: null,
      fixedToUtc: null,
      recipients: [],
      outputFormat: 'xlsx'
    };
  }

  private emptyGrant(): Omit<ReportAccessGrant, 'reportAccessGrantID'> {
    return {
      reportKey: '*',
      userID: null,
      role: null,
      organisationID: null,
      canView: true,
      canExport: false,
      canSchedule: false,
      canViewSensitiveFields: false
    };
  }

  private toLocalInput(value: string): string {
    const date = new Date(value);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  }

  private isEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private saveFile(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private async handleBlobError(error: HttpErrorResponse, fallback: string): Promise<void> {
    if (error.error instanceof Blob && error.error.type.includes('json')) {
      try {
        const body = JSON.parse(await error.error.text());
        this.handleError({ ...error, error: body }, fallback);
        return;
      } catch { /* fall through */ }
    }
    this.handleError(error, fallback);
  }

  private handleError(error: any, fallback: string): void {
    const status = Number(error?.status || 0);
    const body = error?.error;
    const detail = body?.message || body?.title || (typeof body === 'string' ? body : '') || error?.message;
    this.errorMessage = detail || fallback;
    if (body?.errors && typeof body.errors === 'object') {
      for (const [key, value] of Object.entries(body.errors)) {
        this.fieldErrors[key] = Array.isArray(value) ? value.join(' ') : String(value);
      }
    }
    if (status === 403) {
      this.showSaveEditor = false;
      this.showScheduleEditor = false;
      this.showAccessEditor = false;
      this.errorMessage = 'Your report access changed. The restricted editor was closed.';
      this.reporting.getCatalogue(true).subscribe({ next: catalogue => this.catalogue = catalogue });
    } else if (status === 404) {
      this.errorMessage = 'This report item no longer exists.';
    } else if (status === 409) {
      this.errorMessage = detail || 'This report is already running or has a conflicting update. Your form has been kept.';
    }
  }
}
